import * as PIXI from 'pixi.js'
import { AnimatedGIF } from '@pixi/gif'

export interface LoadedMediaResource {
  animation?: AnimatedGIF
  texture?: PIXI.Texture
}

const mediaResourceCache = new Map<string, LoadedMediaResource>()
const loadingPromises = new Map<string, Promise<LoadedMediaResource>>()

export const getCachedMediaResource = (url: string): LoadedMediaResource | undefined => {
  return mediaResourceCache.get(url)
}

export const deleteCachedMediaResource = (url: string): void => {
  mediaResourceCache.delete(url)
  loadingPromises.delete(url)
}

export const pruneMediaResourceCache = (activeUrls: string[]): void => {
  const activeUrlSet = new Set(activeUrls)
  for (const url of mediaResourceCache.keys()) {
    if (!activeUrlSet.has(url)) {
      deleteCachedMediaResource(url)
    }
  }
}

const getUrlExtension = (url: string): string => {
  const path = url.split('?')[0]
  const extension = path.split('.').pop()
  return extension?.toLowerCase() ?? ''
}

const loadImageFromUrl = async (url: string): Promise<PIXI.Texture> => {
  const img = new Image()
  img.crossOrigin = 'anonymous' // img.src starts the fetch - set img.crossOrigin first so resource is fetched with CORS
  img.src = url
  const imageResource = new PIXI.ImageResource(img, { autoLoad: false })
  const loadedImage = await imageResource.load()
  img.remove()

  if (!loadedImage) {
    throw new Error('Error loading image element')
  }

  return new PIXI.Texture(new PIXI.BaseTexture(loadedImage))
}

const loadVideoFromUrl = (url: string): Promise<PIXI.Texture> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video')
    video.crossOrigin = 'anonymous'
    video.muted = true
    video.loop = true
    video.src = url

    video.addEventListener('error', (errorEvent: ErrorEvent) => {
      video.remove()
      reject(errorEvent.error ?? new Error('Error loading video element'))
    })

    video.addEventListener('loadeddata', async () => {
      try {
        const videoResource = new PIXI.VideoResource(video, { autoLoad: false })
        const loadedVideo = await videoResource.load()
        video.remove()

        if (!loadedVideo) {
          reject(new Error('Error loading video element'))
          return
        }

        resolve(new PIXI.Texture(new PIXI.BaseTexture(loadedVideo)))
      } catch (error) {
        video.remove()
        reject(error)
      }
    })
  })
}

const loadGifFromUrl = async (url: string): Promise<AnimatedGIF> => {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error('Unable to load resource')
  }

  const buffer = await response.arrayBuffer()
  return AnimatedGIF.fromBuffer(buffer)
}

const loadBlobUrl = async (url: string): Promise<LoadedMediaResource> => {
  const blob = await (await fetch(url)).blob()

  if (blob.type === 'image/gif') {
    const buffer = await blob.arrayBuffer()
    return { animation: AnimatedGIF.fromBuffer(buffer) }
  }

  if (blob.type.startsWith('image')) {
    return { texture: await loadImageFromUrl(url) }
  }

  if (blob.type.startsWith('video')) {
    return { texture: await loadVideoFromUrl(url) }
  }

  throw new Error('Incompatible resource type')
}

const loadRemoteUrl = async (url: string): Promise<LoadedMediaResource> => {
  const extension = getUrlExtension(url)

  if (extension === 'gif') {
    return { animation: await loadGifFromUrl(url) }
  }

  if (extension === 'mp4' || extension === 'mov' || extension === 'webm') {
    return { texture: await loadVideoFromUrl(url) }
  }

  return { texture: await loadImageFromUrl(url) }
}

const loadMediaResourceInternal = async (url: string): Promise<LoadedMediaResource> => {
  if (url.startsWith('blob:')) {
    return loadBlobUrl(url)
  }

  return loadRemoteUrl(url)
}

export const loadMediaResource = async (url: string): Promise<LoadedMediaResource> => {
  const cached = mediaResourceCache.get(url)
  if (cached) {
    return cached
  }

  const loading = loadingPromises.get(url)
  if (loading) {
    return loading
  }

  const promise = loadMediaResourceInternal(url)
    .then((resource) => {
      mediaResourceCache.set(url, resource)
      loadingPromises.delete(url)
      return resource
    })
    .catch((error) => {
      loadingPromises.delete(url)
      throw error
    })

  loadingPromises.set(url, promise)
  return promise
}
